/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import Pagination from 'react-js-pagination';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import { q, Kpi } from './kit';
import { formatLastActive } from '../../../utils/utils';

const LIMIT = 20;
// Newest first is the log's opening order. It is held in state (rather than left to the
// endpoint's own default) so the caret on screen always names the ordering being asked for.
const DEFAULT_SORT_ORDER = 'DESC';
const SORT_FIELD = 'created_at';

// The "Activity log" tab. Mirrors the retired standalone /user-activity-logs page: the raw
// app-access log (`user_app_access_log`), i.e. every New Open / Resume of the mobile app.
// The old event-logs tab (`user_event_logs`) that also carried this label is gone.
export const EVENT_TYPES = [
  { value: '', label: 'Event Type(All)' },
  { value: '1', label: 'New Open' },
  { value: '2', label: 'Resume' },
];

export const eventTypeLabel = (v) => {
  const n = Number(v);
  if (n === 1) return 'New Open';
  if (n === 2) return 'Resume';
  return '-';
};

const AppAccessTab = ({ userId, from, to, authGet, initialEventType = '' }) => {
  const [eventType, setEventType] = useState(initialEventType || '');
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [page, setPage] = useState(1);

  // Any change to the window, the user or the event filter restarts at page 1.
  // The sort survives all of them — it is the reader's choice, not part of the scope.
  useEffect(() => { setPage(1); }, [userId, from, to, eventType]);

  // The server does the ordering, so a re-sort must return to page 1: page 3 of the
  // old order is a meaningless slice of the new one. Same toggle rule as DetailList —
  // the first click on a column goes ASC, re-clicking the active one flips it.
  const toggleSort = () => {
    setSortOrder((o) => (o === 'ASC' ? 'DESC' : 'ASC'));
    setPage(1);
  };

  const { status, data, reload } = useTabData(async () => {
    const res = await authGet(
      `/admin/api/get-user-app-access-logs?${q(userId, from, to, {
        page, limit: LIMIT, event_type: eventType, sortField: SORT_FIELD, sortOrder,
      })}`,
    );
    return { rows: res.data.data || [], total: res.data.totalItem || 0 };
  }, [userId, from, to, eventType, page, sortOrder]);

  const filters = (
    <div className="au-filters" data-testid="ud-app-access-filters">
      <div className="au-field">
        <span className="au-field__label">Event type</span>
        <select
          className="au-select"
          value={eventType}
          aria-label="Event type filter"
          data-testid="ud-app-access-event-type"
          onChange={(e) => setEventType(e.target.value)}
        >
          {EVENT_TYPES.map((o) => <option key={o.value || 'all'} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {eventType !== '' && (
        <div className="au-field au-field--actions">
          <button type="button" className="au-btn au-btn--ghost" onClick={() => setEventType('')}>Clear</button>
        </div>
      )}
    </div>
  );

  if (status === 'loading') return <div data-testid="ud-app-access">{filters}<TabLoading /></div>;
  if (status === 'error') return <div data-testid="ud-app-access">{filters}<TabError onRetry={reload} /></div>;

  const { rows, total } = data;

  return (
    <div data-testid="ud-app-access">
      {filters}

      <div className="ud-kpis ud-kpis--2">
        <Kpi label="Logs" value={total} />
      </div>

      {!rows.length && page === 1 ? (
        <TabEmpty
          title="No activity"
          message="This user has no app opens or resumes in the selected window."
          icon="◈"
        />
      ) : (
        <div className="au-panel">
          <div className="au-panel__head"><h4 className="au-panel__title">Activity log</h4></div>
          <div className="au-table-wrap">
            <table className="au-table" data-testid="ud-app-access-table">
              <thead>
                <tr>
                  <th className="tnum">No</th>
                  <th>Event type</th>
                  <th
                    className="au-th-sort"
                    data-testid="ud-app-access-sort-created-at"
                    aria-sort={sortOrder === 'ASC' ? 'ascending' : 'descending'}
                    onClick={toggleSort}
                  >
                    Activity Datetime (UTC) {sortOrder === 'ASC' ? '🔼' : '🔽'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td data-label="No" className="tnum">{r.id}</td>
                    <td data-label="Event type">{eventTypeLabel(r.event_type)}</td>
                    <td data-label="Activity Datetime (UTC)" className="tnum">{formatLastActive(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > LIMIT && (
            <div className="ud-pager">
              <Pagination
                activePage={page}
                itemsCountPerPage={LIMIT}
                totalItemsCount={total}
                pageRangeDisplayed={5}
                onChange={setPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppAccessTab;
